/**
 * 邮百工GUI渲染逻辑统一 - 增强版Markdown渲染组件
 * 在核心渲染基础上添加额外功能，如代码复制、内容折叠等
 */

import React, { useState, useCallback, useMemo, useEffect, Component } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import EnhancedMermaid from './EnhancedMermaid';
import { processContent, extractCodeBlocks } from '../core/ContentUtils';
import { registerLanguageAsync, isLanguageRegistered } from '../../utils/syntaxHighlighterRegistry';
// A2UI交互式UI组件导入
import { hasA2UIMarkers, parseA2UIContent, renderMixedContent } from './A2UIParser';
import { A2UIContainer } from './A2UIComponents';
import './A2UIComponents.css';

/**
 * 内置错误边界组件 - 防止 KaTeX 等渲染错误导致整个页面崩溃
 */
class MarkdownErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[EnhancedMarkdown] 渲染错误被捕获:', error.message);
    console.error('[EnhancedMarkdown] 错误详情:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // 降级显示原始内容
      return (
        <div style={{ 
          padding: '8px', 
          backgroundColor: 'rgba(255, 200, 100, 0.1)', 
          borderRadius: '4px',
          border: '1px solid rgba(255, 200, 100, 0.3)',
          fontSize: '0.9em'
        }}>
          <div style={{ color: '#ffa500', marginBottom: '4px', fontSize: '0.85em' }}>
            ⚠️ 内容渲染出现问题，显示原始文本：
          </div>
          <pre style={{ 
            whiteSpace: 'pre-wrap', 
            wordBreak: 'break-word',
            margin: 0,
            fontFamily: 'inherit'
          }}>
            {this.props.fallbackContent || '内容无法显示'}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const CodeBlock = React.memo(function CodeBlock({
  inline,
  className,
  children,
  deferMermaid = false,
  codeClass,
  compactMode,
  showCodeBlockHeader,
  copiedCode,
  onCopy,
}) {
  const match = /language-(\w+)/.exec(className || "");
  const codeContent = (children == null ? '' : String(children)).replace(/\n$/, "");
  const language = match?.[1] || "";
  const isMermaidBlock = !inline && language === 'mermaid';
  const shouldHighlight = !inline && Boolean(language) && !isMermaidBlock;
  const [isReady, setIsReady] = useState(() => isLanguageRegistered(language));

  useEffect(() => {
    if (!shouldHighlight) return;
    let isActive = true;
    registerLanguageAsync(language).then((ok) => {
      if (isActive && ok) {
        setIsReady(true);
      }
    });
    return () => {
      isActive = false;
    };
  }, [language, shouldHighlight]);

  if (!inline && !codeContent.trim()) {
    return null;
  }

  if (isMermaidBlock) {
    if (deferMermaid) {
      return (
        <div
          className="mermaid-streaming-placeholder"
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            minHeight: 76,
            margin: '8px 0',
            padding: '14px 16px',
            color: '#aebbd0',
            fontSize: 13,
            border: '1px dashed rgba(110, 231, 183, 0.32)',
            borderRadius: 10,
            background: 'rgba(110, 231, 183, 0.06)',
          }}
        >
          <span
            className="mermaid-streaming-dot"
            style={{
              width: 8,
              height: 8,
              flex: '0 0 auto',
              borderRadius: '50%',
              background: '#6ee7b7',
              boxShadow: '0 0 0 4px rgba(110, 231, 183, 0.13)',
            }}
          />
          正在生成图表，将在图表完整后展示
        </div>
      );
    }
    return <EnhancedMermaid chart={codeContent} zoomEnabled={true} />;
  }

  if (shouldHighlight) {
    return (
      <div className={`code-block-wrapper ${codeClass}`} style={compactMode ? {margin: '2px 0'} : undefined}>
        {showCodeBlockHeader && (
          <div className="code-block-header">
            <span className="code-language">{language}</span>
            <button 
              className={`copy-code-button ${copiedCode === codeContent.substring(0, 15) ? 'copied' : ''}`}
              onClick={() => onCopy(codeContent)}
              title="复制代码"
            >
              {copiedCode === codeContent.substring(0, 15) ? '已复制' : '复制'}
            </button>
          </div>
        )}
        {isReady ? (
          <SyntaxHighlighter
            language={language}
            style={vscDarkPlus}
            customStyle={{
              margin: 0, 
              borderRadius: showCodeBlockHeader ? '0 0 4px 4px' : '4px',
              ...(compactMode ? {padding: '8px'} : {})
            }}
            showLineNumbers={codeContent.split('\n').length > 5}
            wrapLines={true}
          >
            {codeContent}
          </SyntaxHighlighter>
        ) : (
          <pre className="code-block-loading">{codeContent}</pre>
        )}
      </div>
    );
  }

  return (
    <code className={`${className} inline-code ${codeClass}`} style={compactMode ? {padding: '1px 3px'} : undefined}>
      {children}
    </code>
  );
});

/**
 * 增强型Markdown渲染组件
 * 
 * @param {Object} props 组件属性
 * @param {string} props.content Markdown内容
 * @param {string} props.className 自定义CSS类名
 * @param {boolean} props.collapsible 是否可折叠
 * @param {number} props.collapseThreshold 折叠阈值
 * @param {Function} props.onContentClick 内容点击回调
 * @param {Array} props.types 内容类型数组
 * @param {boolean} props.inlineMode 是否为内联模式
 * @param {boolean} props.compactMode 是否使用紧凑模式(减小间距)
 * @param {Object} props.renderOptions 渲染选项
 * @param {boolean} props.showCodeBlockHeader 是否显示代码块头部
 * @param {Function} props.onA2UIAction A2UI动作回调
 */
const EnhancedMarkdown = ({ 
  content, 
  className = '', 
  collapsible = false, 
  collapseThreshold = 15, 
  onContentClick = null, 
  renderOptions = {}, 
  types = [], 
  inlineMode = false, 
  compactMode = false, 
  showCodeBlockHeader = true,
  // A2UI回调
  onA2UIAction = null,
  // CC 文件链接拦截回调：(url, filename) => void
  onFileClick = null,
  // 禁用 Mermaid 检测（CC 聊天文本场景，避免误判和内容损坏）
  disableMermaid = false,
  // 流式消息中，未闭合的 Mermaid 围栏不应触发渲染或错误提示。
  isStreaming = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsible);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // 处理代码复制 - 将useCallback移动到组件顶部，避免条件执行
  const handleCopyCode = useCallback((code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        // 显示复制成功提示
        setCopiedCode(code.substring(0, 15));
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch(err => {
        console.error('复制失败:', err);
      });
  }, []);
  
  // A2UI动作处理
  const handleA2UIAction = useCallback((action) => {
    console.log('A2UI Action (EnhancedMarkdown):', action);
    if (onA2UIAction) {
      onA2UIAction(action);
    }
  }, [onA2UIAction]);
  
  // 解构渲染选项
  const {
    customClass = '',
    tableClass = '',
    codeClass = '',
    nestingLevel = 0
  } = renderOptions;
  
  // 处理内容并获取类型信息
  // disableMermaid=true 时跳过 mermaid 检测，直接作为普通 markdown 渲染（避免 CC 文本被误处理）
  const { content: processedContent, types: contentTypes } = useMemo(() => {
    if (disableMermaid) {
      return { content, types: ['markdown'] };
    }
    return processContent(content);
  }, [content, disableMermaid]);

  const hasUnclosedMermaidFence = useMemo(() => {
    let inMermaid = false;
    String(content || '').split(/\r?\n/).forEach((line) => {
      if (!inMermaid && /^\s*```mermaid\s*$/i.test(line)) {
        inMermaid = true;
      } else if (inMermaid && /^\s*```\s*$/.test(line)) {
        inMermaid = false;
      }
    });
    return inMermaid;
  }, [content]);

  const deferMermaid = isStreaming && hasUnclosedMermaidFence;
  
  // 计算内容行数
  const lineCount = useMemo(() => content.split('\n').length, [content]);

  // 如果内容为空，返回空元素
  if (!content) return null;
  
  // 判断是否需要折叠
  const shouldBeCollapsible = collapsible && lineCount > collapseThreshold;
  
  // 处理折叠切换
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  // 处理内容点击
  const handleContentClick = (e) => {
    // 如果点击的是链接或按钮，不处理
    if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON') {
      return;
    }
    
    // 调用外部回调
    if (onContentClick) {
      onContentClick(e);
    }
  };
  
  // 预处理：修复公式中的转义下划线和方括号
  // LLM 输出时可能对 _ 进行 Markdown 转义变成 \_，但 KaTeX 不认识 \_
  const preprocessMathContent = (text) => {
    if (!text) return text;
    
    // 处理块级公式 $$...$$
    let result = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
      // 在公式中将 \_ 替换为 _，将 \[ 和 \] 替换为 [ 和 ]
      const fixed = formula
        .replace(/\\_/g, '_')
        .replace(/\\\[/g, '[')
        .replace(/\\\]/g, ']');
      return '$$' + fixed + '$$';
    });
    
    // 处理行内公式 $...$ (单个$，非连续的$$)
    // 使用更安全的方式：逐个匹配单个$包裹的内容
    result = result.replace(/\$([^$\n]+?)\$/g, (match, formula) => {
      // 在公式中将 \_ 替换为 _
      const fixed = formula.replace(/\\_/g, '_');
      return '$' + fixed + '$';
    });
    
    return result;
  };
  
  // 转义 Markdown 链接文本中的下划线，防止 _ 被解析为斜体导致链接失效
  // 只处理 [text](url) 中的 text 部分，不影响 URL 和其他正文内容
  // 注意：URL 中可能有括号嵌套（如 Wikipedia 链接），使用非贪婪且限制范围的正则
  const escapeLinkTextUnderscores = (text) => {
    if (!text) return text;
    // 只转义链接文本里的下划线，URL 原样保留
    // [^[\]]+  匹配链接文本（不含方括号）
    // \([^)]*\) 不够安全，改为只匹配到行尾第一个完整括号对内的内容
    return text.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, (match, linkText, url) => {
      // 先还原已有的 \_ 再重新转义，保证幂等
      const escapedText = linkText.replace(/\\_/g, '_').replace(/_/g, '\\_');
      return `[${escapedText}](${url})`;
    });
  };

  // 创建Markdown渲染组件
  const renderMarkdown = (content) => {
    // disableMermaid=true（CC聊天场景）时：
    //   把链接文本 [text](url) 中 text 部分的 _ 替换为 HTML 实体 &#95;
    //   防止 Markdown 解析器把链接文本里的 _ 当斜体标记处理，导致链接解析失败/URL被篡改。
    //   URL 部分完全不动。
    // 其他场景：做完整的下划线转义 + 数学公式预处理。
    let processedContent;
    if (disableMermaid) {
      // 只替换 [linkText](url) 中 linkText 里的 _，URL 原样保留
      processedContent = content.replace(
        /\[([^\]]*_[^\]]*)\]\((https?:\/\/[^)]+)\)/g,
        (match, linkText, url) => `[${linkText.replace(/_/g, '&#95;')}](${url})`
      );
    } else {
      processedContent = preprocessMathContent(escapeLinkTextUnderscores(content));
    }
    
    return (
      <MarkdownErrorBoundary fallbackContent={content}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, [rehypeKatex, { throwOnError: false, strict: 'ignore' }]]}
        components={{
          // 代码块处理
          code({ node, inline, className, children, ...props }) {
            return (
              <CodeBlock
                inline={inline}
                className={className}
                children={children}
                deferMermaid={deferMermaid}
                codeClass={codeClass}
                compactMode={compactMode}
                showCodeBlockHeader={showCodeBlockHeader}
                copiedCode={copiedCode}
                onCopy={handleCopyCode}
              />
            );
          },
          
          // 段落处理 - 处理内联模式和compactMode
          p({ node, children, ...props }) {
            if (inlineMode) {
              return <span className="inline-paragraph" {...props}>{children}</span>;
            }
            return <p 
              {...props} 
              style={compactMode ? {margin: '0 0 6px 0', padding: 0, lineHeight: '1.5'} : undefined}
            >
              {children}
            </p>;
          },
          
          // 标题处理
          h1({ node, children, ...props }) {
            return <h1 
              {...props} 
              style={compactMode ? {
                marginTop: '8px', 
                marginBottom: '6px', 
                fontSize: '18px',
                lineHeight: '1.5'
              } : undefined}
            >
              {children}
            </h1>;
          },
          
          h2({ node, children, ...props }) {
            return <h2 
              {...props} 
              style={compactMode ? {
                marginTop: '8px', 
                marginBottom: '6px', 
                fontSize: '16px',
                lineHeight: '1.5'
              } : undefined}
            >
              {children}
            </h2>;
          },
          
          h3({ node, children, ...props }) {
            return <h3 
              {...props} 
              style={compactMode ? {
                marginTop: '7px', 
                marginBottom: '5px', 
                fontSize: '15px',
                lineHeight: '1.5'
              } : undefined}
            >
              {children}
            </h3>;
          },
          
          h4({ node, children, ...props }) {
            return <h4 
              {...props} 
              style={compactMode ? {
                marginTop: '7px', 
                marginBottom: '5px', 
                fontSize: '14px',
                lineHeight: '1.5'
              } : undefined}
            >
              {children}
            </h4>;
          },
          
          // 列表处理
          ul({ node, children, ...props }) {
            return <ul 
              {...props} 
              style={compactMode ? {
                marginTop: '5px', 
                marginBottom: '6px', 
                paddingLeft: '20px'
              } : undefined}
            >
              {children}
            </ul>;
          },
          
          ol({ node, children, ...props }) {
            return <ol 
              {...props} 
              style={compactMode ? {
                marginTop: '5px', 
                marginBottom: '6px', 
                paddingLeft: '20px'
              } : undefined}
            >
              {children}
            </ol>;
          },
          
          li({ node, children, ...props }) {
            return <li 
              {...props} 
              style={compactMode ? {
                marginBottom: '4px'
              } : undefined}
            >
              {children}
            </li>;
          },
          
          // 表格处理 - 添加响应式支持和compactMode
          table({ node, children, ...props }) {
            return (
              <div className={`table-responsive ${tableClass}`} style={compactMode ? {margin: '6px 0'} : undefined}>
                <table {...props} style={compactMode ? {margin: 0} : undefined}>
                  {children}
                </table>
              </div>
            );
          },
          
          // 表格头和单元格
          th({ node, children, ...props }) {
            return <th {...props} style={compactMode ? {padding: '4px 8px'} : undefined}>{children}</th>;
          },
          
          td({ node, children, ...props }) {
            return <td {...props} style={compactMode ? {padding: '4px 8px'} : undefined}>{children}</td>;
          },

          img({ node, ...props }) {
            return (
              <img
                {...props}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  ...(props.style || {}),
                }}
              />
            );
          },
          
          // 链接处理：claw/files 链接拦截为内联预览，其余新窗口打开
          a({ href, children, ...props }) {
            if (onFileClick && href && href.includes('claw/files')) {
              const filename = href.split('/').pop() || href;
              return (
                <span
                  onClick={(e) => { e.preventDefault(); onFileClick(href, filename); }}
                  style={{
                    color: "#58a6ff",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationStyle: "dotted",
                  }}
                  title={`点击在面板内预览: ${filename}`}
                >
                  {children}
                </span>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          },

          // 引用块
          blockquote({ node, children, ...props }) {
            return <blockquote 
              {...props} 
              style={compactMode ? {
                margin: '6px 0', 
                paddingLeft: '12px',
                borderLeft: '3px solid #444'
              } : undefined}
            >
              {children}
            </blockquote>;
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
      </MarkdownErrorBoundary>
    );
  };
  
  // A2UI增强渲染：检测并处理A2UI标记
  const renderContentWithA2UI = (contentToRender) => {
    // 检查是否包含A2UI标记
    if (hasA2UIMarkers(contentToRender)) {
      const normalizedContent = contentToRender.replace(
        /```(?:markdown)?\s*\n([\s\S]*?:::[\s\S]*?)\n```/g,
        (_match, body) => body
      );
      const { markdown, components } = parseA2UIContent(normalizedContent);
      
      // 使用 A2UIContainer 包裹，提供 FormContext
      return (
        <A2UIContainer onAction={handleA2UIAction}>
          {renderMixedContent(markdown, components, (md) => renderMarkdown(md))}
        </A2UIContainer>
      );
    }
    
    // 无A2UI标记，正常渲染Markdown
    return renderMarkdown(contentToRender);
  };
  
  // 内联模式下的渲染
  if (inlineMode) {
    return (
      <span className={`enhanced-markdown inline-mode ${className} ${customClass}`}>
        {renderContentWithA2UI(content)}
      </span>
    );
  }
  
  // 主要内容渲染
  return (
    <div className={`enhanced-markdown ${className} ${inlineMode ? 'inline-mode' : ''} ${compactMode ? 'compact-mode' : ''}`} 
      onClick={handleContentClick}
      style={compactMode ? { 
        margin: 0, 
        padding: 0,
        lineHeight: '1.3'
      } : undefined}
    >
      {/* 可折叠内容的折叠指示器 */}
      {shouldBeCollapsible && (
        <div 
          className="collapse-indicator"
          onClick={toggleCollapse}
        >
          {isCollapsed ? '展开更多 ▼' : '收起内容 ▲'}
        </div>
      )}
      
      {/* 内容区域 */}
      <div className="markdown-content-area">
        {renderContentWithA2UI(
          isCollapsed && shouldBeCollapsible 
            ? content.split('\n').slice(0, collapseThreshold).join('\n') 
            : content
        )}
        
        {/* 折叠状态下的渐变遮罩 */}
        {isCollapsed && shouldBeCollapsible && (
          <div className="content-fade-overlay"></div>
        )}
      </div>
      
      {/* 加载相关样式 */}
      <style jsx>{`
        .enhanced-markdown {
          position: relative;
          width: 100%;
        }
        
        .enhanced-markdown.inline-mode {
          display: inline;
          width: auto;
          position: static;
        }
        
        .inline-mode :global(.inline-paragraph) {
          display: inline;
          margin: 0;
          padding: 0;
        }
        
        .markdown-content-area {
          width: 100%;
          overflow: hidden;
        }
        
        .table-responsive {
          width: 100%;
          overflow-x: auto;
          margin-bottom: ${compactMode ? '1px' : '4px'};
          -webkit-overflow-scrolling: touch;
        }
        
        .code-block-wrapper {
          margin: ${compactMode ? '1px 0' : '4px 0'};
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid var(--border-color, #ddd);
        }
        
        .code-block-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: ${compactMode ? '0.3rem 0.8rem' : '0.5rem 1rem'};
          background-color: var(--code-header-bg, #2d2d2d);
          color: var(--code-header-color, #e6e6e6);
          font-family: var(--font-mono, monospace);
          font-size: ${compactMode ? '0.8rem' : '0.85rem'};
        }
        
        .copy-code-button {
          background-color: transparent;
          border: 1px solid var(--button-border, #666);
          border-radius: 4px;
          color: var(--button-color, #e6e6e6);
          padding: ${compactMode ? '0.15rem 0.4rem' : '0.25rem 0.5rem'};
          cursor: pointer;
          font-size: ${compactMode ? '0.7rem' : '0.75rem'};
          transition: all 0.2s ease;
        }
        
        .copy-code-button:hover {
          background-color: var(--button-hover-bg, #444);
        }
        
        .copy-code-button.copied {
          background-color: var(--button-success-bg, #2e7d32);
          color: var(--button-success-color, #fff);
        }
        
        .collapse-indicator {
          text-align: center;
          padding: ${compactMode ? '3px' : '5px'};
          background: var(--collapse-indicator-bg, rgba(0,0,0,0.03));
          border-radius: 4px;
          margin-bottom: ${compactMode ? '4px' : '8px'};
          cursor: pointer;
          font-size: 0.85rem;
          color: var(--collapse-indicator-color, #666);
        }
        
        .collapse-indicator:hover {
          background: var(--collapse-indicator-hover-bg, rgba(0,0,0,0.05));
        }
        
        .content-fade-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: linear-gradient(transparent, var(--fade-overlay-color, white));
          pointer-events: none;
        }
        
        .enhanced-markdown :global(p) {
          margin: ${compactMode ? '1px 0' : '4px 0'};
        }
        
        /* 控制标题间距 */
        .enhanced-markdown :global(h1),
        .enhanced-markdown :global(h2),
        .enhanced-markdown :global(h3),
        .enhanced-markdown :global(h4),
        .enhanced-markdown :global(h5),
        .enhanced-markdown :global(h6) {
          margin-top: ${compactMode ? '0.5em' : '0.5em'} !important; /* 使用 !important 提高优先级 */
          margin-bottom: ${compactMode ? '0.3em' : '0.25em'} !important;
        }

        /* 控制段落间距 */
        .enhanced-markdown :global(p) {
          margin-top: 0 !important; /* 强制顶部无间距 */
          margin-bottom: ${compactMode ? '0.3em' : '0.25em'} !important; /* 强制底部小间距 */
        }
        
        /* 控制列表间距 */
        .enhanced-markdown :global(ul),
        .enhanced-markdown :global(ol) {
          margin-top: ${compactMode ? '0.3em' : '0.25em'} !important;
          margin-bottom: ${compactMode ? '0.3em' : '0.25em'} !important;
          padding-left: 20px !important; /* 保持内边距 */
        }

        /* 控制列表项间距 */
        .enhanced-markdown :global(li) {
          margin-top: 0 !important;
          margin-bottom: ${compactMode ? '0.2em' : '0.1em'} !important; /* 增加垂直间距 */
        }
      `}</style>
    </div>
  );
};

export default React.memo(EnhancedMarkdown);
